import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { isControlled as isControlledFn, safeRest, uniqueId } from '@wds/utils';
import { SIZES, INPUT_COLORS } from '@wds/helpers';
import { Label } from '@wds/internal-components';

import InputErrorMessage from './InputErrorMessage';

import useStyles from './useStyles';

const Input = React.forwardRef(
  (
    {
      ariaLabel,
      defaultValue,
      disabled,
      color,
      errorMessage,
      id,
      inputSize,
      label,
      name,
      onBlur,
      onChange,
      readOnly,
      size,
      stretched,
      type,
      value,
      ...otherProps
    },
    ref
  ) => {
    const theme = useTheme();
    const { wrenchInput } = useStyles({
      theme,
      size,
      stretched,
      color,
      disabled,
    });
    const [stateValue, setStateValue] = useState(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const isControlled = useMemo(() => isControlledFn(value), []);
    const inputID = useMemo(() => id || uniqueId('input'), [id]);

    const handleChange = (event) => {
      if (isControlled && event.target.value !== value) {
        event.persist();
        setStateValue(event.target.value);
        if (onChange) {
          onChange(event);
        }
      }
    };

    return (
      <>
        <If condition={label}>
          <Label htmlFor={inputID}>{label}</Label>
        </If>
        <input
          aria-label={ariaLabel}
          className={wrenchInput}
          defaultValue={defaultValue}
          id={inputID}
          name={name}
          onBlur={onBlur}
          onChange={handleChange}
          readOnly={readOnly}
          ref={ref}
          size={inputSize}
          type={type}
          value={isControlled ? value : stateValue}
          {...safeRest(otherProps)}
        />
        <If condition={errorMessage}>
          <InputErrorMessage errorMessage={errorMessage} color={color} />
        </If>
      </>
    );
  }
);

Input.propTypes = {
  /**
   * Override for aria-label if it is different from label
   */
  ariaLabel: PropTypes.string,
  /**
   * Color variants
   */
  color: PropTypes.oneOf(INPUT_COLORS),
  /**
   * Provide default value on load
   */
  defaultValue: PropTypes.node,
  /**
   * If true, input is disabled from use
   */
  disabled: PropTypes.bool,
  /**
   * Provide an error message for validation
   */
  errorMessage: PropTypes.node,
  /**
   * identifier for input component
   */
  // eslint-disable-next-line consistent-return
  id: (props, propName) => {
    if (
      typeof props[propName] !== 'string' &&
      typeof props.label !== 'string' &&
      typeof props.ariaLabel !== 'string'
    ) {
      return new Error(
        'Accessibility Error: If `label` or `ariaLabel` is not provided the `id` prop is required.'
      );
    }
  },
  /**
   * Maximum size of input value
   */
  inputSize: PropTypes.number,
  /**
   * It is best practice to compliment a text input with a label.
   */
  label: PropTypes.node,
  /**
   * Name attribute of the input
   */
  name: PropTypes.string,
  /**
   * onBlur handler
   * function(event: object) => void
   */
  onBlur: PropTypes.func,
  /**
   * onChange handler
   * function(event: object) => void
   */
  onChange: PropTypes.func,
  /**
   * If true, input values are read only and cannot be modified
   */
  readOnly: PropTypes.bool,
  /**
   * Input size variants
   */
  size: PropTypes.oneOf(SIZES),
  /**
   * If true will fit the full width of parent container
   */
  stretched: PropTypes.bool,
  /**
   * Type of the input element. Refer to https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types
   */
  type: PropTypes.string,
  /**
   * Value of input component
   */
  value: PropTypes.node,
};

Input.defaultProps = {
  ariaLabel: undefined,
  color: undefined,
  defaultValue: undefined,
  disabled: false,
  errorMessage: undefined,
  id: undefined,
  inputSize: undefined,
  label: undefined,
  name: undefined,
  onBlur: undefined,
  onChange: undefined,
  readOnly: false,
  size: 'md',
  stretched: false,
  type: 'text',
  value: undefined,
};

Input.displayName = 'Input';

export default Input;
